import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Registration = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        email: '', password: '', role: '',
        name: '', contact_no: '', location: '', latitude: '', longitude: '',
        opens_at: '', closes_at: '', operating_days: '', service_radius_km: '',
        // Manufacturer-specific fields
        num_machines: '', usable_area_sqft: '', num_workers: '',
        available_shifts: '', capacity: '',
        // Legal documents
        gstin: '', aadhar_no: '', pan_no: '', fssai_no: '',
        gst_doc: null, aadhar_doc: null, pan_doc: null, fssai_doc: null
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleFileChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.files[0] });
        setError('');
    };

    // Handle shift checkbox toggle
    const handleShiftToggle = (shiftTime) => {
        const currentShifts = formData.available_shifts ? formData.available_shifts.split(',') : [];
        const idx = currentShifts.indexOf(shiftTime);
        if (idx > -1) {
            currentShifts.splice(idx, 1);
        } else {
            currentShifts.push(shiftTime);
        }
        setFormData({ ...formData, available_shifts: currentShifts.filter(s => s).join(',') });
    };

    const getLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFormData({
                        ...formData,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                    alert("Location fetched successfully!");
                },
                (error) => {
                    console.error(error);
                    alert("Unable to retrieve your location");
                }
            );
        } else {
            alert("Geolocation is not supported by your browser");
        }
    };

    const handleRoleSelect = (role) => {
        setFormData({ ...formData, role });
        setStep(3);
    };

    const handleNext = (e) => {
        e.preventDefault();
        setError('');

        if (step === 1) {
            if (!formData.email || !formData.password) return setError("Please fill in all fields");
            setStep(2);
        } else if (step === 3) {
            if (!formData.name || !formData.contact_no || !formData.location) return setError("Please fill in all details");
            setStep(4);
        } else if (step === 4) {
            if (!formData.opens_at || !formData.closes_at || !formData.operating_days || !formData.service_radius_km) return setError("Please fill in business hours");
            // For manufacturers, go to step 5 (Manufacturer Details)
            // For distributors, skip to step 6 (Legal)
            if (formData.role === 'manufacturer') {
                setStep(5);
            } else {
                setStep(6);
            }
        } else if (step === 5) {
            // Manufacturer details validation
            if (!formData.num_machines || !formData.usable_area_sqft || !formData.num_workers || !formData.available_shifts || !formData.capacity) {
                return setError("Please fill in all manufacturer details");
            }
            setStep(6);
        } else if (step === 6) {
            // Validate required documents if necessary, or just proceed
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        try {
            // Build FormData object for File Uploads
            const data = new FormData();
            Object.keys(formData).forEach(key => {
                data.append(key, formData[key]);
            });

            // Send as Multipart Form Data (default for FormData object)
            const response = await axios.post('https://rotica.in/register.php', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                setSuccess("Registration Successful! Review pending.");
            } else {
                setError(response.data.message || "Registration failed");
            }
        } catch (err) {
            console.error(err);
            if (err.response) {
                setError(`Server Error: ${err.response.status} - ${JSON.stringify(err.response.data)}`);
            } else if (err.request) {
                setError("Network Error: No response. Check Headers/CORS or URL.");
            } else {
                setError(`Error: ${err.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registration-container">
            <div className="registration-card">
                <div className="brand-header">
                    <h1>Rotica</h1>
                    <p>Intelligent Service</p>
                </div>

                {success ? (
                    <div className="success-message">
                        <h2>Registration Complete!</h2>
                        <p>{success}</p>
                    </div>
                ) : (
                    <>
                        <div className="form-header">
                            {step === 1 && <h2>Create Account</h2>}
                            {step === 2 && <h2>Account Type</h2>}
                            {step === 3 && <h2>Basic Profile</h2>}
                            {step === 4 && <h2>Operations</h2>}
                            {step === 5 && <h2>Manufacturer Details</h2>}
                            {step === 6 && <h2>Legal & Documents</h2>}
                        </div>

                        <form onSubmit={handleNext}>
                            {/* STEP 1: CREDENTIALS */}
                            {step === 1 && (
                                <>
                                    <div className="input-group">
                                        <label>Email address</label>
                                        <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Password</label>
                                        <div className="password-wrapper">
                                            <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} required />
                                            <button type="button" className="eye-icon" onClick={() => setShowPassword(!showPassword)}>{showPassword ? "👁️" : "👁️‍🗨️"}</button>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* STEP 2: ROLE */}
                            {step === 2 && (
                                <div className="role-selection">
                                    <button type="button" className="role-btn" onClick={() => handleRoleSelect('distributor')}>Distributor</button>
                                    <button type="button" className="role-btn" onClick={() => handleRoleSelect('manufacturer')}>Manufacturer</button>
                                </div>
                            )}

                            {/* STEP 3: PROFILE & LOCATION */}
                            {step === 3 && (
                                <>
                                    <div className="input-group">
                                        <label>Company / Name</label>
                                        <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Contact Number</label>
                                        <input type="tel" name="contact_no" value={formData.contact_no} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Location</label>
                                        <input type="text" name="location" value={formData.location} onChange={handleChange} required />
                                        <button type="button" onClick={getLocation} style={{ marginTop: '10px', padding: '5px' }}>📍 Get My Location</button>
                                        {formData.latitude && <small style={{ color: 'green', display: 'block' }}>Lat: {formData.latitude}, Lng: {formData.longitude}</small>}
                                    </div>
                                </>
                            )}

                            {/* STEP 4: OPERATIONS */}
                            {step === 4 && (
                                <>
                                    <div className="input-group">
                                        <label>Opens At</label>
                                        <input type="time" name="opens_at" value={formData.opens_at} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Closes At</label>
                                        <input type="time" name="closes_at" value={formData.closes_at} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Operating Days (e.g., Mon,Tue,Wed)</label>
                                        <input type="text" name="operating_days" value={formData.operating_days} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Service Radius (km)</label>
                                        <input type="number" name="service_radius_km" value={formData.service_radius_km} onChange={handleChange} required />
                                    </div>
                                </>
                            )}

                            {/* STEP 5: MANUFACTURER DETAILS (only for manufacturers) */}
                            {step === 5 && (
                                <>
                                    <div className="input-group">
                                        <label>Number of Machines</label>
                                        <input type="number" name="num_machines" placeholder="e.g., 5" value={formData.num_machines} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Total Usable Area in Plant (sq ft)</label>
                                        <input type="number" name="usable_area_sqft" placeholder="e.g., 2000" value={formData.usable_area_sqft} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Number of Workers</label>
                                        <input type="number" name="num_workers" placeholder="e.g., 10" value={formData.num_workers} onChange={handleChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Available Shifts</label>
                                        <div className="shift-checkboxes" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                                            <label className="shift-option" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_shifts.includes('7-10am')}
                                                    onChange={() => handleShiftToggle('7-10am')}
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                                <span>Batch 1: 7AM - 10AM</span>
                                            </label>
                                            <label className="shift-option" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_shifts.includes('12-3pm')}
                                                    onChange={() => handleShiftToggle('12-3pm')}
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                                <span>Batch 2: 12PM - 3PM</span>
                                            </label>
                                            <label className="shift-option" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_shifts.includes('6-9pm')}
                                                    onChange={() => handleShiftToggle('6-9pm')}
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                                <span>Batch 3: 6PM - 9PM</span>
                                            </label>
                                        </div>
                                        {formData.available_shifts && (
                                            <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                                                Selected: {formData.available_shifts}
                                            </small>
                                        )}
                                    </div>
                                    <div className="input-group">
                                        <label>Estimated Capacity (in 100's/unit)</label>
                                        <input type="number" name="capacity" placeholder="e.g., 5 (means 500 units)" value={formData.capacity} onChange={handleChange} required />
                                    </div>
                                </>
                            )}

                            {/* STEP 6: LEGAL & DOCUMENTS */}
                            {step === 6 && (
                                <>
                                    <div className="input-group">
                                        <label>GSTIN & Document</label>
                                        <input type="text" name="gstin" placeholder="GSTIN No" value={formData.gstin} onChange={handleChange} required />
                                        <input type="file" name="gst_doc" accept="image/*,application/pdf" onChange={handleFileChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>Aadhar & Document</label>
                                        <input type="text" name="aadhar_no" placeholder="Aadhar No" value={formData.aadhar_no} onChange={handleChange} required />
                                        <input type="file" name="aadhar_doc" accept="image/*,application/pdf" onChange={handleFileChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>PAN & Document</label>
                                        <input type="text" name="pan_no" placeholder="PAN No" value={formData.pan_no} onChange={handleChange} required />
                                        <input type="file" name="pan_doc" accept="image/*,application/pdf" onChange={handleFileChange} required />
                                    </div>
                                    <div className="input-group">
                                        <label>FSSAI & Document</label>
                                        <input type="text" name="fssai_no" placeholder="FSSAI No" value={formData.fssai_no} onChange={handleChange} required />
                                        <input type="file" name="fssai_doc" accept="image/*,application/pdf" onChange={handleFileChange} required />
                                    </div>
                                </>
                            )}

                            {error && <p className="error-text">{error}</p>}

                            {step !== 2 && (
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? "Processing..." : (step === 6 ? "Complete Registration" : "Next")}
                                </button>
                            )}

                            {step === 1 && (
                                <button type="button" className="forgot-password" onClick={() => navigate('/login')}>Already have an account? Log In</button>
                            )}
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default Registration;
